import { Component, OnDestroy, OnInit } from '@angular/core';
import { DropdownOptionDto } from 'src/app/_models/dropdown-option.dto';
import { AppraisalManagerTableItem } from '../settings/models/appraisal-manager-table-item';
import { AppraisalRelationshipStatus } from '../settings/models/appraisal-relationship-status.enum';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { AppraisalEmployeeResponseDto } from '../settings/models/appraisal-employee-response.dto';
import { EmployeeUserDropdownDto } from '../settings/models/appraisal-settings.dto';
import { of, Subscription, throwError } from 'rxjs';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { AppraisalSettingsService } from '../settings/services/appraisal-settings.service';
import { catchError, finalize, tap } from 'rxjs/operators';

type AssignmentStatusOption = {
  labelKey: string;
  value: AppraisalRelationshipStatus | null;
};

@Component({
  selector: 'appraisals-settings',
  templateUrl: './appraisals-settings.component.html',
  styleUrls: ['./appraisals-settings.component.scss'],
  standalone: false
})
export class AppraisalsSettingsComponent implements OnInit, OnDestroy {
  // Data
  filteredManagers: AppraisalManagerTableItem[] = [];
  managers: AppraisalManagerTableItem[] = [];

  editors: DropdownOptionDto[] = [];
  selectedEditors: DropdownOptionDto[] = [];
  currentEditors: DropdownOptionDto[] = [];

  employees: EmployeeUserDropdownDto[] = [];
  employeesWithoutCurrentManager: EmployeeUserDropdownDto[] = [];
  selectedEmployees: EmployeeUserDropdownDto[] = [];
  currentEmployees: EmployeeUserDropdownDto[] = [];

  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  first: number = 0;

  // State variables
  isLoading: boolean = false;
  isLoadingDropdowns: boolean = false;
  isLoadingAssignments: boolean = false;
  isSubmittingAssignments: boolean = false;
  isSubmittingManagers: boolean = false;
  employeePopupVisible = false;
  selectedEmployee: AppraisalEmployeeResponseDto | null = null;
  // Add / remove manager popup
  assignManagersPopupVisible = false;
  // Update assigned employees for manager popup
  updateAssignmentsPopupVisible = false;
  selectedManager: AppraisalManagerTableItem | null = null;
  expandedRowKeys: Record<string, boolean> = {};
  managerStatusFilters: Record<number, AppraisalRelationshipStatus | null> = {};

  // Filters
  managerFilter: string = '';
  currentUserId = 0;
  isAdminOrPortalUser = false;
  currentUserIsAdminManaged = false;
  canManageManagerList = false;

  private subscriptions: Subscription[] = [];

  // Status dropdown options
  statusOptions: AssignmentStatusOption[] = [
    { labelKey: 'ALL_STATUSES', value: null },
    {
      labelKey: 'PENDING',
      value: AppraisalRelationshipStatus.Pending
    },
    {
      labelKey: 'ACTIVE',
      value: AppraisalRelationshipStatus.Active
    },
    {
      labelKey: 'REJECTED',
      value: AppraisalRelationshipStatus.Rejected
    },
    {
      labelKey: 'REVOKED',
      value: AppraisalRelationshipStatus.Revoked
    }
  ];

  constructor(
    private translate: TranslateService,
    private confirmationService: ConfirmationService,
    private appraisalSettingsService: AppraisalSettingsService,
    private messageService: MessageService
  ) {}

  //#region Life cycle hooks

  /**
   * On component initialization, loads the list of managers and the data for manager and employee dropdowns.
   * The managers are loaded first to ensure that the main table is populated as soon as possible,
   * while the dropdown data is loaded in parallel to populate the options for adding/removing managers and
   * updating assignments.
   */
  ngOnInit(): void {
    this.currentUserId = +BaseServices.UserId;
    this.isAdminOrPortalUser =
      +BaseServices.roleId === 1 || +BaseServices.roleId === 2;

    this.loadManagers();
    this.loadManagerAndEmployeeDropdowns();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  //#endregion

  //#region load data

  /**
   * Loads the list of managers for the business and applies any active filters.
   * This method is called on component initialization
   * and after updating the list of managers to refresh the displayed data.
   */
  loadManagers() {
    this.isLoading = true;
    this.subscriptions.push(
      this.appraisalSettingsService
        .getManagers(BaseServices.BusinessId)
        .subscribe({
          next: response => {
            this.managers = response.map(m => ({
              Id: m.Id,
              Name: m.Name,
              AssignmentCount: m.AssignmentCount,
              IsAdminManaged: m.IsAdminManaged,
              HasFetchedEmployees: false,
              AssignedEmployees: [],
              FilteredEmployees: []
            }));
            this.expandedRowKeys = {};
            this.managerStatusFilters = {};
            this.currentUserIsAdminManaged = this.managers.some(
              manager => manager.Id === this.currentUserId && manager.IsAdminManaged
            );
            this.canManageManagerList =
              this.isAdminOrPortalUser || this.currentUserIsAdminManaged;
            this.applyManagerFilter();
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          }
        })
    );
  }

  /**
   * Loads the data for manager and employee dropdowns used in the assign managers and update assignments popups.
   * This method is called on component initialization to ensure that the dropdowns are populated with the latest data.
   * It sets the loading state while fetching the data and handles any errors that may occur during the API call.
   */
  loadManagerAndEmployeeDropdowns() {
    this.isLoadingDropdowns = true;
    this.subscriptions.push(
      this.appraisalSettingsService
        .getBusinessSettings(BaseServices.BusinessId)
        .subscribe({
          next: response => {
            this.editors = response.Editors;
            this.employees = response.Employees;
            this.isLoadingDropdowns = false;
          },
          error: () => {
            this.isLoadingDropdowns = false;
          }
        })
    );
  }

  /**
   * Loads the assigned employees for a specific manager.
   * This method is called when expanding a manager row in the table to fetch and display the assigned employees.
   * @param manager The manager for which to load assigned employees
   * @returns An observable that emits the loaded assigned employees
   */
  loadAssignedEmployeesForManager(manager: AppraisalManagerTableItem) {
    this.isLoadingAssignments = true;

    return this.appraisalSettingsService.getAssignedEmployees(manager.Id).pipe(
      tap(response => {
        manager.AssignedEmployees = response;
        manager.HasFetchedEmployees = true;
        this.initializeManagerStatusFilter(manager.Id);
        this.applyAssignedEmployeesStatusFilter(manager);
        this.isLoadingAssignments = false;
      }),
      catchError(error => {
        this.isLoadingAssignments = false;
        return throwError(() => error);
      })
    );
  }

  //#endregion

  //#region Event Handlers

  /**
   * Toggles the expansion of a manager row in the table. If expanding and the employees for that
   * manager have not been fetched yet, it triggers the loading of assigned employees for that manager.
   * @param manager The manager whose row is being toggled
   * @param expanded Whether the row is currently expanded or not
   */
  toggleRow(manager: AppraisalManagerTableItem, expanded: boolean) {
    if (expanded) {
      const nextState = { ...this.expandedRowKeys };
      delete nextState[manager.Id];
      this.expandedRowKeys = nextState;
      return;
    }

    this.expandedRowKeys = {
      ...this.expandedRowKeys,
      [manager.Id]: true
    };

    if (!manager.HasFetchedEmployees) {
      this.loadAssignedEmployeesForManager(manager).subscribe({
        error: () => {
          const nextState = { ...this.expandedRowKeys };
          delete nextState[manager.Id];
          this.expandedRowKeys = nextState;
        }
      });
    }
  }

  /**
   * Applies the manager name filter to the list of managers. This method is called when the filter input changes
   */
  onFilterChange() {
    this.applyManagerFilter();
  }

  /**
   * Clears the manager name filter and resets the filtered managers list to show all managers.
   * This method is called when the clear filters button is clicked
   */
  clearFilters() {
    this.managerFilter = '';
    this.applyManagerFilter();
  }

  /**
   * Opens assign managers popup and populates selectedEditors array based on
   * current managers to show which editors are currently assigned as managers
   */
  openAssignManagers() {
    if (!this.canManageManagerList) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('APPRAISAL_ADMIN_MANAGED_WARNING')
      });
      return;
    }

    this.assignManagersPopupVisible = true;

    this.populateSelectedEditors();
  }

  /**
   * Opens employee details popup and sets selected employee to show details of the clicked employee
   * @param employee Employee to show details for
   */
  openEmployeePopup(employee: AppraisalEmployeeResponseDto) {
    this.selectedEmployee = employee;
    this.employeePopupVisible = true;
  }

  /**
   * Opens update assignments popup and sets selected manager to show assigned employees for that manager
   * @param manager Manager to show assigned employees for
   */
  openUpdateAssignments(manager: AppraisalManagerTableItem) {
    if (this.isManagerLockedForCurrentUser(manager)) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('APPRAISAL_ADMIN_MANAGED_WARNING')
      });
      return;
    }

    this.selectedManager = manager;
    this.updateAssignmentsPopupVisible = true;

    const load$ = manager.HasFetchedEmployees
      ? of(null)
      : this.loadAssignedEmployeesForManager(manager);

    load$.subscribe(() => this.populateSelectedEmployees());
    this.filterEmployeesWithoutCurrentManager(manager.Id);
  }

  //#endregion

  //#region Add / Remove Managers

  /**
   * Populates selectedEditors array based on current managers. This is used to show which
   * editors are currently assigned as managers when opening the assign managers popup.
   */
  populateSelectedEditors() {
    const currentManagerIds: number[] = this.managers.map(x => x.Id);

    this.selectedEditors = this.editors.filter(e =>
      currentManagerIds.includes(e.Id)
    );

    this.currentEditors = structuredClone(this.selectedEditors);
  }

  /**
   * Updates the managers list based on selected editors.
   * If no changes are made, the popup is closed without any action.
   * If changes are made, a confirmation dialog is shown before updating.
   */
  onUpdateManagers() {
    if (this.isSubmittingManagers) {
      return;
    }

    const managerIdsToPersist = this.getManagerIdsForUpdate();
    const currentManagerIds = this.currentEditors.map(editor => editor.Id);

    const isSame =
      this.areIdSetsEqual(currentManagerIds, managerIdsToPersist);

    // If no changes, close popup
    if (isSame) {
      this.assignManagersPopupVisible = false;
      return;
    }

    const removed = currentManagerIds.filter(
      currentManagerId => !managerIdsToPersist.includes(currentManagerId)
    );

    const updateManagersMessage = this.translate.instant(
      'UPDATE_APPRAISAL_MANAGERS_MESSAGE'
    );

    const messageArray: string[] = [];

    if (removed.length > 0) {
      messageArray.push(
        this.translate.instant(
          'REMOVE_APPRAISAL_MANAGERS_REVOKE_WARNING_MESSAGE'
        )
      );
    }

    messageArray.push(updateManagersMessage);

    this.confirmationService.confirm({
      message: messageArray.join('<br><br/>'),
      accept: () => {
        this.isSubmittingManagers = true;
        this.appraisalSettingsService
          .updateManagersForBusiness(
            BaseServices.BusinessId,
            managerIdsToPersist
          )
          .pipe(
            finalize(() => {
              this.isSubmittingManagers = false;
            })
          )
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translate.instant(
                  'UPDATE_APPRAISAL_MANAGERS_SUCCESS_MESSAGE'
                )
              });
              this.assignManagersPopupVisible = false;
              this.clearFilters();
              this.loadManagers();
            },
            error: () => {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant(
                  'UPDATE_APPRAISAL_MANAGERS_ERROR_MESSAGE'
                )
              });
            }
          });
      }
    });
  }

  //#endregion

  //#region Update Assigned Employees

  /**
   * Populates selectedEmployees array based on current assigned employees of the selected manager.
   * This is used to show which employees are currently assigned to the manager when opening the update assignments popup.
   * It filters out employees with revoked status, as they are not considered currently assigned.
   */
  populateSelectedEmployees() {
    const currentAssignedEmployeeIds: number[] =
      this.selectedManager.AssignedEmployees.filter(
        emp => emp.Status !== AppraisalRelationshipStatus.Revoked
      ).map(emp => emp.EmployeeUserId);

    this.selectedEmployees = this.employees.filter(e =>
      currentAssignedEmployeeIds.includes(e.Id)
    );

    this.currentEmployees = structuredClone(this.selectedEmployees);
  }

  /**
   * Updates the assigned employees list for the selected manager.
   * If no changes are made, the popup is closed without any action.
   * If changes are made, a confirmation dialog is shown before updating.
   */
  onUpdateAssignments() {
    const isSame =
      this.currentEmployees.length === this.selectedEmployees.length &&
      this.currentEmployees.every(ce =>
        this.selectedEmployees.some(se => se.Id == ce.Id)
      );

    // If no changes, close popup
    if (isSame) {
      this.updateAssignmentsPopupVisible = false;
      return;
    }

    const removed = this.currentEmployees.filter(
      ce => !this.selectedEmployees.some(se => se.Id === ce.Id)
    );

    const updateAssignmentsMessage = this.translate.instant(
      'UPDATE_APPRAISAL_ASSIGNMENTS_SEND_NOTIFICATION_MESSAGE'
    );

    const messageArray: string[] = [];

    if (removed.length > 0) {
      messageArray.push(
        this.translate.instant(
          'REMOVE_APPRAISAL_ASSIGNMENTS_REVOKE_WARNING_MESSAGE'
        )
      );
    }

    messageArray.push(updateAssignmentsMessage);

    this.confirmationService.confirm({
      message: messageArray.join('<br><br/>'),
      accept: () => {
        this.isSubmittingAssignments = true;
        this.appraisalSettingsService
          .updateEmployeesForManager(
            this.selectedManager.Id,
            this.selectedEmployees.map(e => e.Id)
          )
          .pipe(
            finalize(() => {
              this.isSubmittingAssignments = false;
            })
          )
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translate.instant(
                  'UPDATE_APPRAISAL_ASSIGNMENTS_SUCCESS_MESSAGE'
                )
              });

              this.recalculateAssignmentCount(this.selectedManager);
              this.updateAssignmentsPopupVisible = false;
            },
            error: () => {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant(
                  'UPDATE_APPRAISAL_ASSIGNMENTS_ERROR_MESSAGE'
                )
              });
            }
          });
      }
    });
  }

  /**
   * Recalculates the assignment count for a given manager.
   * This method is called after updating assignments to ensure the displayed count is accurate.
   * @param manager Manager for which to recalculate the assignment count
   */
  recalculateAssignmentCount(manager: AppraisalManagerTableItem) {
    this.loadAssignedEmployeesForManager(manager).subscribe({
      next: () => {
        manager.AssignmentCount =
          manager.AssignedEmployees.filter(
            emp => emp.Status !== AppraisalRelationshipStatus.Revoked
          ).length || 0;
      },
      error: () => {
        manager.HasFetchedEmployees = false;
      }
    });
  }

  //#endregion

  //#region Helper Methods

  /**
   * Gets translation string based on statusId from the enum
   * @param statusId Relationship status enum
   * @returns translation string
   */
  getStatusText(statusId: AppraisalRelationshipStatus): string {
    const typeMap: { [key: number]: string } = {
      [AppraisalRelationshipStatus.Pending]: 'PENDING',
      [AppraisalRelationshipStatus.Active]: 'ACTIVE',
      [AppraisalRelationshipStatus.Rejected]: 'REJECTED',
      [AppraisalRelationshipStatus.Revoked]: 'REVOKED'
    };
    return typeMap[statusId] || 'UNKNOWN';
  }

  getStatusLabelKey(statusId: AppraisalRelationshipStatus): string {
    return this.getStatusText(statusId);
  }

  onAssignedEmployeesStatusFilterChange(
    manager: AppraisalManagerTableItem,
    selectedStatus: AppraisalRelationshipStatus | null
  ): void {
    this.managerStatusFilters = {
      ...this.managerStatusFilters,
      [manager.Id]: selectedStatus ?? null
    };
    this.applyAssignedEmployeesStatusFilter(manager);
  }

  /**
   * Gets joined string of the departments. Also have collapse functionality for big arrays
   * and long joined strings.
   * @param departments Departments to extract names from
   * @returns String of department names
   */
  getDepartmentsText(departments: DropdownOptionDto[]): string {
    if (departments.length === 0) return '';

    const names = departments.map(d => d.Name.trim()).filter(Boolean);
    const joined = names.join(', ');

    const TOO_MANY = 4;
    const MAX_LENGTH = 45;

    if (names.length >= TOO_MANY || joined.length > MAX_LENGTH) {
      return `${names.length} ` + this.translate.instant('DEPARTMENTS');
    }

    return joined;
  }

  /**
   * Appends manager name to the header of update assignments popup
   * @returns Header for update assignment popup
   */
  getUpdateAssignmentsPopupHeader(): string {
    const baseHeader = this.translate.instant('EDIT_ASSIGNMENTS');
    const managerName = this.selectedManager ? this.selectedManager.Name : '';

    return `${baseHeader} - ${managerName}`;
  }

  /**
   * Refreshes the list of employees without the currently selected manager. This is used to ensure that the
   * manager is not shown in the list of employees when updating assignments for that manager, as it would
   * not make sense to assign a manager as their own employee.
   * @param managerId ID of the currently selected manager to be excluded from the employees list
   */
  private filterEmployeesWithoutCurrentManager(managerId: number) {
    this.employeesWithoutCurrentManager = this.employees
      .filter(emp => emp.Id !== managerId)
      .sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
  }

  private initializeManagerStatusFilter(managerId: number) {
    if (this.managerStatusFilters[managerId] !== undefined) {
      return;
    }

    this.managerStatusFilters = {
      ...this.managerStatusFilters,
      [managerId]: null
    };
  }

  private applyAssignedEmployeesStatusFilter(manager: AppraisalManagerTableItem): void {
    const selectedStatus = this.getManagerSelectedStatus(manager.Id);
    manager.FilteredEmployees = selectedStatus === null
      ? [...manager.AssignedEmployees]
      : manager.AssignedEmployees.filter(employee => employee.Status === selectedStatus);
  }

  getManagerSelectedStatus(managerId: number): AppraisalRelationshipStatus | null {
    return Object.prototype.hasOwnProperty.call(this.managerStatusFilters, managerId)
      ? this.managerStatusFilters[managerId]
      : null;
  }

  /**
   * Applies the manager name filter to the list of managers. This method is called when the filter input changes
   */
  private applyManagerFilter() {
    const filterValue = this.managerFilter.trim().toLowerCase();
    const matchingManagers = filterValue
      ? this.managers.filter(manager =>
          manager.Name.toLowerCase().includes(filterValue)
        )
      : this.managers;

    this.filteredManagers = this.sortManagersForDisplay(matchingManagers);

    this.totalItems = this.filteredManagers.length;
    this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 0;
  }

  isCurrentManager(manager: AppraisalManagerTableItem): boolean {
    return manager.Id === this.currentUserId;
  }

  isManagerLockedForCurrentUser(manager: AppraisalManagerTableItem): boolean {
    // Admin/Portal users can always edit any manager's assignments.
    if (this.isAdminOrPortalUser) {
      return false;
    }

    // Every manager can edit their own assignments.
    if (manager.Id === this.currentUserId) {
      return false;
    }

    // A manager that is not admin-locked cannot edit other managers'
    // employee relationships — they may only manage their own.
    if (!this.currentUserIsAdminManaged) {
      return true;
    }

    // Admin-locked managers cannot edit other admin-locked managers.
    return manager.IsAdminManaged;
  }

  isManagerSelectionLocked(editorId: number): boolean {
    const manager = this.managers.find(m => m.Id === editorId);
    if (!manager) {
      return false;
    }

    return this.isManagerLockedForCurrentUser(manager);
  }

  private getManagerIdsForUpdate(): number[] {
    const selectedIds = this.selectedEditors.map(e => e.Id);

    if (this.isAdminOrPortalUser) {
      return Array.from(new Set(selectedIds));
    }

    const lockedManagerIds = this.managers
      .filter(manager => this.isManagerLockedForCurrentUser(manager))
      .map(manager => manager.Id);

    return Array.from(new Set([...selectedIds, ...lockedManagerIds]));
  }

  private sortManagersForDisplay(
    managers: AppraisalManagerTableItem[]
  ): AppraisalManagerTableItem[] {
    return [...managers].sort((left, right) => {
      const leftIsCurrent = this.isCurrentManager(left);
      const rightIsCurrent = this.isCurrentManager(right);

      if (leftIsCurrent !== rightIsCurrent) {
        return leftIsCurrent ? -1 : 1;
      }

      return left.Name.localeCompare(right.Name, undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
  }

  private areIdSetsEqual(a: number[], b: number[]): boolean {
    if (a.length !== b.length) {
      return false;
    }

    const bSet = new Set(b);
    return a.every(id => bSet.has(id));
  }

  //#endregion
}
